// The till's list of its shop's drivers for Sell and Orders (UC-4.R6, UC-66,
// T33, #1004).
//
// A driver is not menu: the till reads the list through its order store,
// which keeps it and answers from it while the server does not (#1001), after
// each unlock and each time Pick a driver opens. It shows the list it has at
// once and replaces it when the answer comes, as the tables do (UC-39).
//
// Whether the screen reads drivers decides what its Send says. A screen that
// reads them sends ?drivers=1, and its store then needs a driver on every
// delivery (DRIVER_REQUIRED). A read that answers 404 naming no field is a
// server older than drivers (#998), which a revert put back. With a list in
// hand the screen keeps it and goes on picking from it: the store judges each
// driver from the rows it kept. With none, the screen sends as before,
// without ?drivers=1, and a delivery goes with no driver.
//
// A manager's own list of drivers is Drivers (T34), from Till settings;
// this one only offers them.

import { api } from '../api/client';
import type { components } from '../api/types';
import { currentDraft, followDrivers } from '../draft/draft.svelte';
import { problemOf } from './answer';

export type Driver = components['schemas']['DriverBody'];

/** Where the last read stands: on its way, answered, or failed. */
export type DriversRead = 'loading' | 'ok' | 'error';

let drivers = $state<Driver[]>([]);
let read = $state<DriversRead>('loading');
// A server older than drivers answered the read: the screen sends as before.
let older = $state(false);
// Reads can overlap, an unlock's and the sheet's, and answer out of order:
// only the latest one asked may replace the list.
let asked = 0;

/** The shop's active drivers in name order, as the store sends them. */
export function currentDrivers(): Driver[] {
	return drivers;
}

/** Where the last read of the list stands. */
export function driversRead(): DriversRead {
	return read;
}

/**
 * Whether this screen's Send says it reads drivers (?drivers=1), so a
 * delivery it sends names one. Only a server older than drivers, with no
 * list kept, makes it send as an older screen does.
 */
export function readsDrivers(): boolean {
	return !older;
}

/** The query a Send carries: drivers=1 while the screen reads drivers. */
export function askDrivers(): string {
	return readsDrivers() ? 'drivers=1' : '';
}

/**
 * The driver the cart names: as the till's list gives him, or, while no read
 * has answered, as the cart kept him, so a cart a restart brought back sends
 * with its driver; the store judges him either way. Null for none.
 */
export function cartDriver(): Driver | null {
	const draft = currentDraft();
	if (!draft.driverId) return null;
	const listed = drivers.find((d) => d.id === draft.driverId);
	if (listed) return listed;
	return read === 'ok' ? null : { id: draft.driverId, name: draft.driverName };
}

/**
 * Whether the cart is a delivery whose first Send must ask which driver. A
 * sent order keeps the driver it was sent with: a later Send adds lines to
 * it and names none.
 */
export function driverNeeded(): boolean {
	const draft = currentDraft();
	return draft.type === 'delivery' && !draft.openOrderId && readsDrivers() && cartDriver() === null;
}

/**
 * Read the list. A failure keeps the list in hand. A 404 that names no field
 * is a server older than drivers: with a list in hand the screen keeps it,
 * and with none it stops asking for a driver, and a cart's driver comes off.
 * True when the cart's driver came off because a manager or the owner
 * removed him.
 */
export async function loadDrivers(): Promise<boolean> {
	const ask = ++asked;
	if (drivers.length === 0 && read !== 'ok') read = 'loading';
	try {
		const { data, error, response } = await api.GET('/api/till/drivers');
		if (ask !== asked) return false;
		if (data && error === undefined) {
			drivers = data.drivers ?? [];
			read = 'ok';
			older = false;
			return followDrivers(drivers);
		}
		if (response.status === 404 && !problemOf(error)?.details?.path) {
			read = 'ok';
			if (drivers.length > 0) return false;
			older = true;
			followDrivers(drivers);
			return false;
		}
	} catch {
		// No answer: the list in hand stays, and the next read decides.
		if (ask !== asked) return false;
	}
	read = 'error';
	return false;
}
